/**
 * チケット管理ページ
 *
 * URLパラメータの customerId に基づいて、対象顧客のチケット残高表示、
 * タイマー形式でのセッション管理、利用履歴の閲覧を提供する。
 * 顧客管理画面からのリンクで遷移する。
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableFooter,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Divider,
  Grid,
  TablePagination,
  Alert,
  IconButton,
} from '@mui/material';
import {
  PlayArrow as PlayIcon,
  Pause as PauseIcon,
  Replay as ResumeIcon,
  Stop as StopIcon,
  Add as AddIcon,
  AccessTime as TimeIcon,
  ArrowBack as ArrowBackIcon,
} from '@mui/icons-material';
import api from '../services/api';

interface Customer {
  id: string;
  customerCode: string;
  name: string;
}

interface BalanceInfo {
  customerId: string;
  totalTickets: number;
  usedTickets: number;
  remainingTickets: number;
}

interface Segment {
  id: string;
  startedAt: string;
  endedAt: string | null;
  durationMinutes: number;
}

interface SessionData {
  id: string;
  customerId: string;
  userId: string;
  status: string;
  totalMinutes: number;
  consumedTickets: number;
  startedAt: string;
  completedAt: string | null;
  segments: Segment[];
}

interface SessionStatus {
  session: SessionData | null;
  elapsedMinutes: number;
  projectedTickets: number;
  displayStatus: 'idle' | 'active' | 'paused';
}

interface HistoryItem {
  id: string;
  customerId: string;
  userId: string;
  status: string;
  totalMinutes: number;
  consumedTickets: number;
  startedAt: string;
  completedAt: string | null;
  segments: Segment[];
  user: { id: string; name: string };
}

function calculateTickets(totalMinutes: number): number {
  if (totalMinutes <= 0) return 0;
  return Math.ceil(totalMinutes / 30) * 0.5;
}

function formatDuration(totalSeconds: number): string {
  const h = Math.floor(totalSeconds / 3600);
  const m = Math.floor((totalSeconds % 3600) / 60);
  const s = totalSeconds % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

function formatElapsedReadable(totalSeconds: number): string {
  const totalMinutes = Math.floor(totalSeconds / 60);
  const h = Math.floor(totalMinutes / 60);
  const m = totalMinutes % 60;
  if (h > 0) return `${h}時間${m}分`;
  return `${m}分`;
}

function formatDateTime(iso: string | null): string {
  if (!iso) return '-';
  const d = new Date(iso);
  return d.toLocaleString('ja-JP', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' });
}

function formatTime(iso: string | null): string {
  if (!iso) return '-';
  const d = new Date(iso);
  return d.toLocaleString('ja-JP', { hour: '2-digit', minute: '2-digit' });
}

const TicketManagementPage: React.FC = () => {
  const { customerId } = useParams<{ customerId: string }>();
  const navigate = useNavigate();
  const [customer, setCustomer] = useState<Customer | null>(null);
  const [balance, setBalance] = useState<BalanceInfo | null>(null);
  const [sessionStatus, setSessionStatus] = useState<SessionStatus | null>(null);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [history, setHistory] = useState<HistoryItem[]>([]);
  const [historyTotal, setHistoryTotal] = useState(0);
  const [historyPage, setHistoryPage] = useState(0);
  const [historyRowsPerPage, setHistoryRowsPerPage] = useState(10);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [grantDialogOpen, setGrantDialogOpen] = useState(false);
  const [grantTickets, setGrantTickets] = useState('');
  const [grantReason, setGrantReason] = useState('');
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    if (!customerId) return;
    const fetchCustomer = async () => {
      try {
        const res = await api.get(`/customers/${customerId}`);
        setCustomer(res.data);
      } catch {
        setError('顧客情報の取得に失敗しました');
      }
    };
    fetchCustomer();
  }, [customerId]);

  useEffect(() => {
    if (!customerId) return;
    fetchBalanceAndStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerId]);

  useEffect(() => {
    if (!customerId) return;
    fetchHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerId, historyPage, historyRowsPerPage]);

  const fetchBalanceAndStatus = useCallback(async () => {
    if (!customerId) return;
    try {
      const [balRes, sesRes] = await Promise.all([
        api.get(`/tickets/balance/${customerId}`),
        api.get(`/tickets/session/${customerId}`),
      ]);
      setBalance(balRes.data);
      setSessionStatus(sesRes.data);
      if (sesRes.data.session) {
        setElapsedSeconds(computeElapsedSeconds(sesRes.data.session.segments, sesRes.data.displayStatus));
      } else {
        setElapsedSeconds(0);
      }
    } catch {
      setError('データの取得に失敗しました');
    }
  }, [customerId]);

  const fetchHistory = async () => {
    if (!customerId) return;
    try {
      const params: Record<string, string | number> = { page: historyPage + 1, limit: historyRowsPerPage, customerId };
      const res = await api.get('/tickets/history', { params });
      setHistory(res.data.data || []);
      setHistoryTotal(res.data.total || 0);
    } catch { /* ignore */ }
  };

  function computeElapsedSeconds(segments: Segment[], displayStatus: string): number {
    let totalSec = 0;
    for (const seg of segments) {
      if (seg.endedAt) {
        const start = new Date(seg.startedAt).getTime();
        const end = new Date(seg.endedAt).getTime();
        totalSec += Math.max(0, Math.floor((end - start) / 1000));
      } else if (displayStatus === 'active') {
        const startTime = new Date(seg.startedAt).getTime();
        const now = Date.now();
        totalSec += Math.max(0, Math.floor((now - startTime) / 1000));
      }
    }
    return totalSec;
  }

  useEffect(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    if (sessionStatus?.displayStatus === 'active' && sessionStatus.session) {
      const segments = sessionStatus.session.segments;
      setElapsedSeconds(computeElapsedSeconds(segments, 'active'));
      timerRef.current = setInterval(() => {
        setElapsedSeconds(computeElapsedSeconds(segments, 'active'));
      }, 1000);
    }
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [sessionStatus?.displayStatus, sessionStatus?.session]);

  const handleAction = async (action: 'start' | 'pause' | 'resume' | 'complete') => {
    if (!customerId) return;
    setLoading(true);
    setError(null);
    if (action === 'pause' || action === 'complete') {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    }
    try {
      await api.post(`/tickets/session/${customerId}/${action}`);
      await fetchBalanceAndStatus();
      if (action === 'complete') {
        await fetchHistory();
      }
    } catch (err: any) {
      const msg = err?.response?.data?.message || 'エラーが発生しました';
      setError(msg);
      if ((action === 'pause' || action === 'complete') && sessionStatus?.displayStatus === 'active' && sessionStatus.session) {
        const segments = sessionStatus.session.segments;
        timerRef.current = setInterval(() => {
          setElapsedSeconds(computeElapsedSeconds(segments, 'active'));
        }, 1000);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleGrantSubmit = async () => {
    if (!customerId || !grantTickets) return;
    setLoading(true);
    setError(null);
    try {
      await api.post('/tickets/grant', {
        customerId,
        tickets: Number(grantTickets),
        reason: grantReason || undefined,
      });
      await fetchBalanceAndStatus();
      setGrantDialogOpen(false);
      setGrantTickets('');
      setGrantReason('');
    } catch (err: any) {
      const msg = err?.response?.data?.message || 'チケット付与に失敗しました';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  const displayStatus = sessionStatus?.displayStatus ?? 'idle';
  const currentElapsedMinutes = Math.floor(elapsedSeconds / 60);
  const currentProjectedTickets = calculateTickets(currentElapsedMinutes);

  // History total consumed tickets
  const historyTotalTickets = history.reduce((sum, item) => sum + Number(item.consumedTickets), 0);

  if (!customerId) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">顧客IDが指定されていません</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 0 }}>
      {/* Header */}
      <Box sx={{ px: 3, pt: 2, pb: 1, borderBottom: '1px solid', borderColor: 'divider', display: 'flex', alignItems: 'center' }}>
        <IconButton onClick={() => navigate('/admin/customers')} size="small" sx={{ mr: 1 }}>
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
          チケット管理
        </Typography>
        {customer && (
          <Typography variant="body2" color="text.secondary" sx={{ ml: 2 }}>
            {customer.customerCode} - {customer.name}
          </Typography>
        )}
      </Box>

      <Box sx={{ p: 3 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Main content: Balance (left) + Timer (right) */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {/* Left: Ticket Balance */}
          <Grid item xs={12} md={3}>
            <Paper sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>チケット残高</Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mt: 1 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="body2" color="text.secondary">保有</Typography>
                  <Typography variant="h5" color="primary" fontWeight="bold">{balance?.totalTickets ?? 0}</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="body2" color="text.secondary">使用済み</Typography>
                  <Typography variant="h5" color="error" fontWeight="bold">{balance?.usedTickets ?? 0}</Typography>
                </Box>
                <Divider />
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="body2" color="text.secondary" fontWeight="bold">残り</Typography>
                  <Typography variant="h4" color="success.main" fontWeight="bold">{balance?.remainingTickets ?? 0}</Typography>
                </Box>
              </Box>
              <Box sx={{ mt: 2 }}>
                <Button
                  variant="outlined"
                  size="small"
                  fullWidth
                  startIcon={<AddIcon />}
                  onClick={() => setGrantDialogOpen(true)}
                >
                  チケット付与
                </Button>
              </Box>
            </Paper>
          </Grid>

          {/* Right: Timer Section */}
          <Grid item xs={12} md={9}>
            <Paper
              sx={{
                p: 2,
                height: '100%',
                background: displayStatus === 'active'
                  ? 'linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%)'
                  : displayStatus === 'paused'
                    ? 'linear-gradient(135deg, #fff3e0 0%, #ffe0b2 100%)'
                    : 'linear-gradient(135deg, #f5f5f5 0%, #eeeeee 100%)',
                border: displayStatus === 'active'
                  ? '2px solid #1976d2'
                  : displayStatus === 'paused'
                    ? '2px solid #ed6c02'
                    : '1px solid #e0e0e0',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <TimeIcon fontSize="small" sx={{ mr: 0.5 }} />
                <Typography variant="subtitle2" fontWeight="bold">現在の利用状況</Typography>
                <Chip
                  label={displayStatus === 'active' ? '利用中' : displayStatus === 'paused' ? '中断中' : '未開始'}
                  color={displayStatus === 'active' ? 'success' : displayStatus === 'paused' ? 'warning' : 'default'}
                  size="small"
                  sx={{ ml: 1.5 }}
                />
              </Box>

              {/* Timer Display */}
              <Box sx={{ textAlign: 'center', my: 1.5 }}>
                <Typography
                  sx={{
                    fontSize: '3rem',
                    fontWeight: 'bold',
                    fontVariantNumeric: 'tabular-nums',
                    color: displayStatus === 'active' ? 'primary.main' : displayStatus === 'paused' ? 'warning.main' : 'text.disabled',
                    letterSpacing: 4,
                    lineHeight: 1.2,
                  }}
                >
                  {formatDuration(elapsedSeconds)}
                </Typography>
              </Box>

              {/* Session Details */}
              {sessionStatus?.session && (
                <Box sx={{ display: 'flex', justifyContent: 'center', gap: 4, mb: 1.5 }}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="caption" color="text.secondary">開始日時</Typography>
                    <Typography variant="body2">{formatDateTime(sessionStatus.session.startedAt)}</Typography>
                  </Box>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="caption" color="text.secondary">経過時間</Typography>
                    <Typography variant="body2">{formatElapsedReadable(elapsedSeconds)}</Typography>
                  </Box>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="caption" color="text.secondary">消費予定</Typography>
                    <Typography variant="body2" fontWeight="bold" color="error.main">{currentProjectedTickets} チケット</Typography>
                  </Box>
                </Box>
              )}

              <Divider sx={{ my: 1.5 }} />

              {/* Action Buttons */}
              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<PlayIcon />}
                  disabled={loading || displayStatus !== 'idle'}
                  onClick={() => handleAction('start')}
                >
                  開始
                </Button>
                <Button
                  variant="contained"
                  color="warning"
                  startIcon={<PauseIcon />}
                  disabled={loading || displayStatus !== 'active'}
                  onClick={() => handleAction('pause')}
                >
                  中断
                </Button>
                <Button
                  variant="contained"
                  color="info"
                  startIcon={<ResumeIcon />}
                  disabled={loading || displayStatus !== 'paused'}
                  onClick={() => handleAction('resume')}
                >
                  再開
                </Button>
                <Button
                  variant="contained"
                  color="error"
                  startIcon={<StopIcon />}
                  disabled={loading || displayStatus === 'idle'}
                  onClick={() => handleAction('complete')}
                >
                  終了
                </Button>
              </Box>
            </Paper>
          </Grid>
        </Grid>

        {/* History */}
        <Paper sx={{ p: 2 }}>
          <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold' }}>
            利用履歴
          </Typography>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>利用日</TableCell>
                  <TableCell>担当者</TableCell>
                  <TableCell align="center">開始時刻</TableCell>
                  <TableCell align="center">終了時刻</TableCell>
                  <TableCell align="right">中断時間</TableCell>
                  <TableCell align="right">実績時間</TableCell>
                  <TableCell align="right">消費チケット</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {history.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      <Typography variant="body2" color="text.secondary">利用履歴はありません</Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  history.map((item) => {
                    const h = Math.floor(item.totalMinutes / 60);
                    const m = item.totalMinutes % 60;
                    const timeStr = h > 0 ? `${h}時間${m}分` : `${m}分`;
                    // 中断時間 = (終了時刻 - 開始時刻) - 実績時間
                    let pauseStr = '-';
                    if (item.completedAt && item.startedAt) {
                      const totalElapsed = Math.floor((new Date(item.completedAt).getTime() - new Date(item.startedAt).getTime()) / 60000);
                      const pauseMinutes = Math.max(0, totalElapsed - item.totalMinutes);
                      if (pauseMinutes > 0) {
                        const ph = Math.floor(pauseMinutes / 60);
                        const pm = pauseMinutes % 60;
                        pauseStr = ph > 0 ? `${ph}時間${pm}分` : `${pm}分`;
                      } else {
                        pauseStr = '';
                      }
                    }
                    return (
                      <TableRow key={item.id}>
                        <TableCell>{item.completedAt ? new Date(item.completedAt).toLocaleDateString('ja-JP') : '-'}</TableCell>
                        <TableCell>{item.user.name}</TableCell>
                        <TableCell align="center">{formatTime(item.startedAt)}</TableCell>
                        <TableCell align="center">{formatTime(item.completedAt)}</TableCell>
                        <TableCell align="right">{pauseStr}</TableCell>
                        <TableCell align="right">{timeStr}</TableCell>
                        <TableCell align="right">{Number(item.consumedTickets)}</TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
              {history.length > 0 && (
                <TableFooter>
                  <TableRow>
                    <TableCell colSpan={6} align="right" sx={{ fontWeight: 'bold', borderTop: '2px solid #e0e0e0' }}>
                      合計
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold', borderTop: '2px solid #e0e0e0' }}>
                      {historyTotalTickets}
                    </TableCell>
                  </TableRow>
                </TableFooter>
              )}
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={historyTotal}
            page={historyPage}
            onPageChange={(_, p) => setHistoryPage(p)}
            rowsPerPage={historyRowsPerPage}
            onRowsPerPageChange={(e) => { setHistoryRowsPerPage(parseInt(e.target.value, 10)); setHistoryPage(0); }}
            rowsPerPageOptions={[10, 20, 50]}
            labelRowsPerPage="表示件数"
          />
        </Paper>
      </Box>

      {/* Grant Tickets Dialog */}
      <Dialog open={grantDialogOpen} onClose={() => setGrantDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>チケット付与</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            {customer?.name} にチケットを付与します。
          </Typography>
          <TextField
            label="チケット数"
            type="number"
            fullWidth
            value={grantTickets}
            onChange={(e) => setGrantTickets(e.target.value)}
            sx={{ mb: 2 }}
            inputProps={{ min: 0.5, step: 0.5 }}
          />
          <TextField
            label="付与理由（任意）"
            fullWidth
            multiline
            rows={2}
            value={grantReason}
            onChange={(e) => setGrantReason(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setGrantDialogOpen(false)}>キャンセル</Button>
          <Button
            variant="contained"
            onClick={handleGrantSubmit}
            disabled={!grantTickets || Number(grantTickets) <= 0 || loading}
          >
            付与
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TicketManagementPage;
